//@flow
import React, {createContext, useContext, useMemo} from 'react';
import {useState} from 'react';

import type {BulkEditProductVariant} from '../../../entities';
import {useProductVariantRequestState} from './productVariantRequest';

type ContextState = {
  enabledVariant: boolean,
  setEnabledVariant: (enabledVariant: boolean) => void,
  productVariant: BulkEditProductVariant,
};

export const ProductVariantContext = createContext<ContextState | null>(null);

type Props = {
  children: React$Node,
  productVariantId: string,
};

export function ProductVariantProvider({children, productVariantId}: Props) {
  const variantList = useProductVariantRequestState();

  const productVariant = useMemo(() => {
    return variantList.find(variant => variant.id === productVariantId);
  }, [variantList, productVariantId]);

  const [enabledVariant, setEnabledVariant] = useState(
    !!(productVariant && productVariant.enabled)
  );

  if (!productVariant) return null;

  return (
    <ProductVariantContext.Provider
      value={{
        enabledVariant,
        setEnabledVariant,
        productVariant,
      }}
    >
      {children}
    </ProductVariantContext.Provider>
  );
}

export function useProductVariantContext(): BulkEditProductVariant {
  const state = useContext(ProductVariantContext);

  if (!state) {
    throw new Error(
      'useProductVariantContext must be used within a ProductVariantProvider'
    );
  }

  return state.productVariant;
}

export function useEnabledVariantContext(): [
  boolean,
  (enabledVariant: boolean) => void
] {
  const state = useContext(ProductVariantContext);

  if (!state) {
    throw new Error(
      'useEnabledVariantContext must be used within a ProductVariantProvider'
    );
  }

  return [state.enabledVariant, state.setEnabledVariant];
}
